import { Users } from 'lucide-react';
import { PALETTE } from '../lib/constants';
import type { ApporteurStats } from '../types';

/**
 * Ventilation par apporteur — affichée uniquement si `stats.total > 0`.
 *
 * 3 cartes :
 *  - Avec apporteur (vert)
 *  - Sans apporteur (gris)
 *  - Contrats via Manu (violet) = apporteurParraine
 *
 * Cf SPEC-PORT.md §7.3.B.
 */
export interface VentilationApporteursProps {
  stats: ApporteurStats;
}

export function VentilationApporteurs({ stats }: VentilationApporteursProps) {
  if (stats.total <= 0) return null;

  const pct = (n: number): string => ((n / stats.total) * 100).toFixed(1);

  return (
    <div className="rounded-stripe border border-stripe-border bg-white p-4 shadow-stripe-sm">
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-stripe-text-muted" />
        <div>
          <div className="text-sm font-semibold text-stripe-text">Ventilation par apporteur</div>
          <div className="mt-0.5 text-2xs text-stripe-text-muted">
            Répartition des contrats souscrits sur la période
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <ApporteurCard
          label="Avec apporteur"
          value={stats.avecApporteur}
          pct={pct(stats.avecApporteur)}
          dotColor={PALETTE.ventilation.avecApporteur}
        />
        <ApporteurCard
          label="Sans apporteur"
          value={stats.sansApporteur}
          pct={pct(stats.sansApporteur)}
          dotColor={PALETTE.ventilation.sansApporteur}
        />
        <ApporteurCard
          label="Contrats via Manu"
          value={stats.apporteurParraine}
          pct={pct(stats.apporteurParraine)}
          dotColor={PALETTE.ventilation.apporteurParraine}
        />
      </div>
    </div>
  );
}

function ApporteurCard({
  label,
  value,
  pct,
  dotColor,
}: {
  label: string;
  value: number;
  pct: string;
  dotColor: string;
}) {
  return (
    <div className="rounded-lg border border-stripe-border-light bg-stripe-bg/30 p-4">
      <div className="flex items-center gap-2">
        <span
          className="inline-block h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: dotColor }}
        />
        <span className="text-2xs font-medium uppercase tracking-wide text-stripe-text-muted">
          {label}
        </span>
      </div>
      <div className="mt-1.5 text-xl font-semibold tracking-tight text-stripe-text tabular-nums">
        {value.toLocaleString('fr-FR')}
      </div>
      <div className="mt-0.5 text-2xs text-stripe-text-muted tabular-nums">{pct}%</div>
    </div>
  );
}
